/**
 * My Studio - Video Editor Component
 * Full-featured video editor for creating social media reels
 * Available for Ultra membership only
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Film, Type, Wand2, Music, Download, Settings, Undo2, Redo2,
  ChevronLeft, Scissors, Copy, Trash2, Save, Loader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ReelsProjectProvider, useProject, useUI, getProjectDuration } from './stores/projectStore';
import Preview from './components/Preview';
import Timeline from './components/Timeline';
import MediaPanel from './components/MediaPanel';
import TextPanel from './components/TextPanel';
import EffectsPanel from './components/EffectsPanel';
import AudioPanel from './components/AudioPanel';
import ExportDialog from './components/ExportDialog';
import { ASPECT_RATIOS } from './utils/effectPresets';
import { formatDuration } from './utils/timeUtils';

// Panel configuration
const PANELS = [
  { id: 'media', label: 'Media', icon: Film },
  { id: 'text', label: 'Text', icon: Type },
  { id: 'effects', label: 'Effects', icon: Wand2 },
  { id: 'audio', label: 'Audio', icon: Music },
];

// Inner editor component (needs context)
const ReelsEditorInner = ({ onBack, project: incomingProject, onSave }) => {
  const { project, actions: projectActions } = useProject();
  const { ui, actions: uiActions } = useUI();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const initialLoadRef = React.useRef(false);

  // Initialize new project on mount
  useEffect(() => {
    if (incomingProject) {
      projectActions.loadProject(incomingProject);
    } else if (!project.id) {
      projectActions.newProject();
    }
  }, [incomingProject, project.id, projectActions]);

  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      return;
    }
    setHasChanges(true);
  }, [project]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Space: Toggle play
      if (e.code === 'Space') {
        e.preventDefault();
        uiActions.setPlaying(!ui.isPlaying);
      }

      // Ctrl/Cmd + Z: Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        // TODO: Implement undo
      }

      // Ctrl/Cmd + Shift + Z: Redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        // TODO: Implement redo
      }

      // Delete/Backspace: Delete selected
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (ui.selectedClipId) {
          for (const track of project.tracks) {
            const clip = track.clips.find(c => c.id === ui.selectedClipId);
            if (clip) {
              projectActions.removeClip(track.id, clip.id);
              uiActions.clearSelection();
              break;
            }
          }
        } else if (ui.selectedTextId) {
          projectActions.removeTextClip(ui.selectedTextId);
          uiActions.clearSelection();
        }
      }

      // S: Split at playhead
      if (e.key === 's' && !e.ctrlKey && !e.metaKey) {
        if (ui.selectedClipId) {
          for (const track of project.tracks) {
            const clip = track.clips.find(c => c.id === ui.selectedClipId);
            if (clip) {
              const splitTime = ui.currentTime;
              if (splitTime > clip.startTime && splitTime < clip.startTime + clip.duration) {
                projectActions.splitClip(track.id, clip.id, splitTime);
                uiActions.clearSelection();
              }
              break;
            }
          }
        }
      }

      // D: Duplicate
      if (e.key === 'd' && !e.ctrlKey && !e.metaKey) {
        if (ui.selectedClipId) {
          for (const track of project.tracks) {
            const clip = track.clips.find(c => c.id === ui.selectedClipId);
            if (clip) {
              const newClip = {
                ...clip,
                id: `clip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                startTime: clip.startTime + clip.duration,
              };
              projectActions.addClip(track.id, newClip);
              break;
            }
          }
        }
      }

      // Escape: Clear selection
      if (e.key === 'Escape') {
        uiActions.clearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [ui, project.tracks, uiActions, projectActions]);

  // Get selected clip info
  const getSelectedClipInfo = useCallback(() => {
    if (!ui.selectedClipId) return null;
    for (const track of project.tracks) {
      const clip = track.clips.find(c => c.id === ui.selectedClipId);
      if (clip) {
        return { track, clip };
      }
    }
    return null;
  }, [ui.selectedClipId, project.tracks]);

  // Delete selected clip
  const handleDeleteClip = useCallback(() => {
    const info = getSelectedClipInfo();
    if (info) {
      projectActions.removeClip(info.track.id, info.clip.id);
      uiActions.clearSelection();
    }
  }, [getSelectedClipInfo, projectActions, uiActions]);

  // Split selected clip at current playhead position
  const handleSplitClip = useCallback(() => {
    const info = getSelectedClipInfo();
    if (!info) return;

    const { track, clip } = info;
    const splitTime = ui.currentTime;

    // Check if playhead is within the clip
    if (splitTime <= clip.startTime || splitTime >= clip.startTime + clip.duration) {
      alert('Move the playhead to where you want to split the clip');
      return;
    }

    projectActions.splitClip(track.id, clip.id, splitTime);
    uiActions.clearSelection();
  }, [getSelectedClipInfo, ui.currentTime, projectActions, uiActions]);

  // Duplicate selected clip
  const handleDuplicateClip = useCallback(() => {
    const info = getSelectedClipInfo();
    if (!info) return;

    const { track, clip } = info;

    // Create a new clip with a new ID, placed right after the original
    const newClip = {
      ...clip,
      id: `clip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      startTime: clip.startTime + clip.duration,
    };

    projectActions.addClip(track.id, newClip);
  }, [getSelectedClipInfo, projectActions]);

  // Render active panel
  const renderPanel = () => {
    switch (ui.activePanel) {
      case 'media':
        return <MediaPanel />;
      case 'text':
        return <TextPanel />;
      case 'effects':
        return <EffectsPanel />;
      case 'audio':
        return <AudioPanel />;
      default:
        return <MediaPanel />;
    }
  };

  const duration = getProjectDuration(project);
  const aspectLabel = `${project.settings.width}x${project.settings.height}`;
  const durationLabel = formatDuration(duration);
  const frameRateLabel = `${project.settings.frameRate} FPS`;
  const statusLabel = isSaving ? 'Saving...' : hasChanges ? 'Unsaved changes' : 'All changes saved';
  const statusTone = isSaving
    ? 'bg-slate-100 text-slate-600'
    : hasChanges
      ? 'bg-amber-100 text-amber-700'
      : 'bg-teal-100 text-teal-700';

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave(project);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save motion project:', error);
      alert('Failed to save project. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [onSave, project]);

  return (
    <div className="flex flex-col h-full bg-[#0b1118]">
      {/* Top toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 bg-slate-900/80 border-b border-slate-800/80 backdrop-blur">
        <div className="flex items-center gap-3">
          {/* Back button */}
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-3 py-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {/* Project name */}
          <input
            type="text"
            value={project.name || 'Untitled Reel'}
            onChange={(e) => projectActions.updateProjectName(e.target.value)}
            className="bg-transparent text-white font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 rounded px-2 py-1"
          />

          {/* Undo/Redo */}
          <div className="flex items-center gap-1 ml-4">
            <button
              className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-30"
              disabled={undoStack.length === 0}
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button
              className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-30"
              disabled={redoStack.length === 0}
            >
              <Redo2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-400">
            <span className="px-2.5 py-1 rounded-full bg-slate-800 text-slate-300">
              {aspectLabel}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-slate-800 text-slate-300">
              {frameRateLabel}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-slate-800 text-slate-300">
              {durationLabel}
            </span>
          </div>

          {onSave && (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusTone}`}>
              {statusLabel}
            </span>
          )}

          {onSave && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                hasChanges
                  ? 'bg-teal-600 hover:bg-teal-500 text-white'
                  : 'bg-slate-800 text-slate-400'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          )}

          {/* Project settings */}
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>

          {/* Export button */}
          <button
            onClick={() => setIsExportOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Main editor area */}
      <div className="flex flex-1 min-h-0">
        {/* Left sidebar - Panels */}
        <div className="w-80 flex-shrink-0 flex flex-col bg-slate-900/90 border-r border-slate-800/80 backdrop-blur">
          {/* Panel tabs */}
          <div className="flex border-b border-slate-800/80">
            {PANELS.map((panel) => (
              <button
                key={panel.id}
                onClick={() => uiActions.setActivePanel(panel.id)}
                className={`flex-1 flex flex-col items-center gap-1 py-3 transition-colors ${
                  ui.activePanel === panel.id
                    ? 'text-teal-300 bg-slate-800/60'
                    : 'text-slate-500 hover:text-white hover:bg-slate-800/30'
                }`}
              >
                <panel.icon className="w-5 h-5" />
                <span className="text-[10px]">{panel.label}</span>
              </button>
            ))}
          </div>

          {/* Panel content */}
          <div className="flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={ui.activePanel}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.15 }}
                className="h-full"
              >
                {renderPanel()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Center - Preview */}
        <div className="flex-1 flex flex-col min-w-0">
          <Preview />
        </div>

        {/* Right sidebar - Inspector (optional, could show clip properties) */}
        {(ui.selectedClipId || ui.selectedTextId) && (
          <div className="w-72 flex-shrink-0 bg-slate-900/90 border-l border-slate-800/80 p-4 backdrop-blur">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white">Properties</h3>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Inspector</span>
            </div>

            {ui.selectedClipId && (() => {
              const clipInfo = getSelectedClipInfo();
              return (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Selected Clip</span>
                  <button
                    onClick={() => uiActions.clearSelection()}
                    className="text-xs text-slate-500 hover:text-white"
                  >
                    Deselect
                  </button>
                </div>

                {/* Clip info */}
                {clipInfo && (
                  <div className="p-3 bg-slate-800 rounded-lg space-y-2">
                    <p className="text-sm text-white font-medium truncate">{clipInfo.clip.name || 'Clip'}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-slate-500">Start:</span>
                        <span className="text-slate-300 ml-1">{clipInfo.clip.startTime.toFixed(2)}s</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Duration:</span>
                        <span className="text-slate-300 ml-1">{clipInfo.clip.duration.toFixed(2)}s</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick actions */}
                <div className="flex gap-2">
                  <button
                    onClick={handleSplitClip}
                    className="flex-1 flex items-center justify-center gap-1 p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-white transition-colors"
                    title="Split clip at playhead (move playhead to split point)"
                  >
                    <Scissors className="w-3 h-3" />
                    Split
                  </button>
                  <button
                    onClick={handleDuplicateClip}
                    className="flex-1 flex items-center justify-center gap-1 p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-white transition-colors"
                    title="Duplicate clip"
                  >
                    <Copy className="w-3 h-3" />
                    Duplicate
                  </button>
                  <button
                    onClick={handleDeleteClip}
                    className="flex-1 flex items-center justify-center gap-1 p-2 bg-red-600/20 hover:bg-red-600/40 rounded-lg text-xs text-red-400 transition-colors"
                    title="Delete clip (or press Delete key)"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </div>

                {/* Keyboard shortcuts hint */}
                <div className="text-[10px] text-slate-600 space-y-0.5">
                  <p><kbd className="px-1 py-0.5 bg-slate-800 rounded">Delete</kbd> Remove clip</p>
                  <p><kbd className="px-1 py-0.5 bg-slate-800 rounded">S</kbd> Split at playhead</p>
                  <p><kbd className="px-1 py-0.5 bg-slate-800 rounded">D</kbd> Duplicate clip</p>
                </div>
              </div>
              );
            })()}

            {ui.selectedTextId && (
              <div>
                <p className="text-sm text-slate-400">Text clip selected</p>
                <p className="text-xs text-slate-600 mt-1">Edit in the Text panel</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom - Timeline */}
      <div className="h-48 flex-shrink-0">
        <Timeline />
      </div>

      {/* Export dialog */}
      <ExportDialog isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} />

      {/* Settings dialog */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsSettingsOpen(false)} />
          <div className="relative w-full max-w-md bg-slate-900 rounded-xl shadow-2xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Project Settings</h2>

            {/* Aspect ratio */}
            <div className="mb-4">
              <label className="text-sm text-slate-400 mb-2 block">Aspect Ratio</label>
              <div className="grid grid-cols-2 gap-2">
                {ASPECT_RATIOS.map((ratio) => (
                  <button
                    key={ratio.id}
                    onClick={() => projectActions.updateSettings({ width: ratio.width, height: ratio.height })}
                    className={`p-3 rounded-lg text-left transition-all ${
                      project.settings.width === ratio.width && project.settings.height === ratio.height
                        ? 'bg-teal-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                  >
                    <div className="text-sm font-medium">{ratio.id}</div>
                    <div className="text-xs opacity-70">{ratio.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Frame rate */}
            <div className="mb-6">
              <label className="text-sm text-slate-400 mb-2 block">Frame Rate</label>
              <select
                value={project.settings.frameRate}
                onChange={(e) => projectActions.updateSettings({ frameRate: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value={24}>24 FPS (Cinematic)</option>
                <option value={30}>30 FPS (Standard)</option>
                <option value={60}>60 FPS (Smooth)</option>
              </select>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main exported component with provider
const ReelsEditor = ({ onBack, featureFlags = {}, project = null, onSave = null }) => {
  // Feature gate check
  if (!featureFlags.hasReels) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-[#0b1118] p-8">
        <div className="max-w-md text-center">
          <Film className="w-16 h-16 text-slate-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">My Studio</h2>
          <p className="text-slate-400 mb-6">
            Create stunning video reels for social media with our professional video editor.
          </p>
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-6">
            <p className="text-amber-400 text-sm">
              This feature is available exclusively for Ultra members.
            </p>
          </div>
          <button
            onClick={onBack}
            className="px-6 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors"
          >
            Upgrade to Ultra
          </button>
        </div>
      </div>
    );
  }

  return (
    <ReelsProjectProvider>
      <ReelsEditorInner onBack={onBack} project={project} onSave={onSave} />
    </ReelsProjectProvider>
  );
};

export default ReelsEditor;
